/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { jsPDF } from 'jspdf';

/**
 * Generate and download a printable PDF from a Voice Diary structured note.
 * Uses jsPDF (already in the project).
 */
export const downloadNotePdf = (note = {}, schema = []) => {
  const s = note.structured || {};
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const marginX = 48;
  const width = doc.internal.pageSize.getWidth() - marginX * 2;
  let y = 56;

  const line = (h = 12) => { y += h; };
  const check = () => {
    if (y > doc.internal.pageSize.getHeight() - 60) {
      doc.addPage();
      y = 56;
    }
  };

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('Nota assistenziale', marginX, y);
  line(22);

  // Header info
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  const created = note.created_at ? new Date(note.created_at).toLocaleString('it-IT') : '';
  if (note.patient_label) { doc.text(`Paziente: ${note.patient_label}`, marginX, y); line(); }
  if (created)             { doc.text(`Data: ${created}`, marginX, y); line(); }
  if (note.author_category){ doc.text(`Ruolo autore: ${note.author_category}`, marginX, y); line(); }
  if (note.tags?.length)   { doc.text(`Tag: ${note.tags.join(', ')}`, marginX, y); line(); }

  line(4);
  doc.setDrawColor(180);
  doc.line(marginX, y, marginX + width, y);
  line(14);

  const writeText = (label, value) => {
    if (!value) return;
    check();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(label, marginX, y);
    line(14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    const lines = doc.splitTextToSize(String(value), width);
    lines.forEach((ln) => { check(); doc.text(ln, marginX, y); line(14); });
    line(4);
  };

  const writeList = (label, items) => {
    if (!items || items.length === 0) return;
    check();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(label, marginX, y);
    line(14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    items.forEach((it) => {
      const chunks = doc.splitTextToSize(`\u2022 ${it}`, width - 12);
      chunks.forEach((c) => { check(); doc.text(c, marginX + 6, y); line(14); });
    });
    line(4);
  };

  // Use schema order to keep output consistent with UI
  const orderedKeys = (schema && schema.length ? schema.map((f) => ({ key: f.key, type: f.type, label: f.label }))
    : [
      { key: 'context', type: 'text', label: 'Contesto' },
      { key: 'activities', type: 'list', label: 'Attività svolte' },
      { key: 'patient_condition', type: 'text', label: 'Condizione del paziente' },
      { key: 'vital_signs_notes', type: 'text', label: 'Parametri vitali' },
      { key: 'food_hydration', type: 'text', label: 'Alimentazione / Idratazione' },
      { key: 'elimination', type: 'text', label: 'Diuresi / Alvo' },
      { key: 'mobility', type: 'text', label: 'Mobilità' },
      { key: 'mood_behavior', type: 'text', label: 'Umore / Comportamento' },
      { key: 'critical_issues', type: 'list', label: 'Criticità rilevate' },
      { key: 'follow_up', type: 'list', label: 'Follow-up' },
      { key: 'notes_extra', type: 'text', label: 'Note aggiuntive' },
    ]);

  orderedKeys.forEach((f) => {
    const v = s[f.key];
    if (f.type === 'list') writeList(f.label, Array.isArray(v) ? v : []);
    else writeText(f.label, v);
  });

  if (note.raw_transcript) {
    check();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Trascrizione originale', marginX, y); line(14);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    const t = doc.splitTextToSize(note.raw_transcript, width);
    t.forEach((ln) => { check(); doc.text(ln, marginX, y); line(11); });
  }

  // Footer
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(
      `SanitaNexus · Nota generata da Diario Vocale · pagina ${i} di ${totalPages}`,
      marginX,
      doc.internal.pageSize.getHeight() - 20,
    );
    doc.setTextColor(0);
  }

  const safeName = (note.patient_label || 'nota').toString()
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'nota';
  const stamp = note.created_at ? new Date(note.created_at).toISOString().slice(0, 10) : 'nota';
  doc.save(`nota-${safeName}-${stamp}.pdf`);
};
